/**
 * Query Editor Utilities
 *
 * Re-exports all utility functions from sub-modules
 */
import { cloneDeep } from 'lodash';
import React from 'react';
import { statusMsg as statusMsgMap } from './constants';
import { formatdate, toFixDecimal } from './formatters';
import { customSortByKeyAsc } from './sorting';

// Re-export all from sub-modules
export * from './formatters';
export * from './sorting';
export * from './query';
export * from './encoding';
export * from './strings';
export * from './errors';
export * from './d3';
export * from './dom';
export * from './constants';

// Import for use in this file
export { statusMsg } from './constants';

/**
 * Gets selected database info
 */
interface DatabaseInfo {
  id: string;
  name: string;
}

export const getSelDB = (
  database: [string, string] | { id?: string; name?: string } | null
): DatabaseInfo => {
  let dbID = '';
  let dbName = '';
  if (Array.isArray(database)) {
    dbID = database?.[0] || '';
    dbName = database?.[1] || '';
  } else if (database) {
    try {
      const { id = '', name = '' } = database || {};
      dbID = id;
      dbName = name;
    } catch (e) {
      console.error('error getting selected database', e);
    }
  }
  return { id: dbID, name: dbName };
};

/**
 * Gets custom label element for cluster size options
 */
const getCustomLabel = (labels: string[]): React.ReactNode =>
  React.createElement(
    'div',
    { className: 'flex ml-5 justify-between' },
    labels.map((item, key) => React.createElement('p', { key, className: 'mr-2' }, item))
  );

/**
 * Cluster size options
 */
export const clusterSizeOptions = [
  { id: 99, name: 'Custom', label: 'Custom' },
  { id: 1, name: getCustomLabel(['XS', '(1 Executor)']), label: 'XS' },
  { id: 2, name: getCustomLabel(['S', '(2 Executors)']), label: 'S' },
  { id: 3, name: getCustomLabel(['M', '(4 Executors)']), label: 'M' },
  { id: 4, name: getCustomLabel(['L', '(8 Executors)']), label: 'L' },
  { id: 5, name: getCustomLabel(['XL', '(16 Executors)']), label: 'XL' },
  { id: 6, name: getCustomLabel(['XXL', '(32 Executors)']), label: 'XXL' },
  { id: 7, name: getCustomLabel(['XXXL', '(64 Executors)']), label: 'XXXL' },
];

/**
 * Cluster size labels
 */
export const clusterSizeLabel: Record<number, string> = {
  1: 'XS',
  2: 'S',
  4: 'M',
  8: 'L',
  16: 'XL',
  32: 'XXL',
  64: 'XXXL',
  99: 'Custom',
};

/**
 * Gets cluster size label from executor count
 */
export const getClusterSizeLabel = (executorCount: number | string): string => {
  if (executorCount === 'NONE') return '-';
  if (executorCount === 0 || executorCount === '0') return '0';
  const named = clusterSizeLabel?.[executorCount as number];
  if (named) return named;
  if (executorCount == null || executorCount === '') return 'Custom';
  return `Custom (${executorCount})`;
};

/**
 * Gets database ID from database object or string
 */
export const getDBId = (db: { id?: string } | string): string => {
  let dbId = '';
  try {
    dbId = ((db as { id?: string })?.id || db || '')?.toString()?.split('@-')?.[0] ?? '';
  } catch (error) {
    console.error('error in util/getDBId : ', error);
  }
  return dbId;
};

/**
 * Gets editor name (order number) from data
 */
export const getEditorName = (data: Record<string, { order_by?: number }>): number => {
  const editorNames = (Object.values(data) || []).map((item) => item?.order_by || 0);
  return Math.max(...editorNames) + 1;
};

/**
 * Rearranges order for duplicate tab
 */
interface OrderedData {
  uuid?: string;
  order_by?: number;
  [key: string]: unknown;
}

export const rearrangeOrderDuplicate = (
  data: Record<string, OrderedData>,
  activeOrder: number,
  newData: Record<string, OrderedData> = {},
  newUUID: string,
  activeUUID: string
): Record<string, OrderedData> => {
  const clonedData = cloneDeep(data);
  const sortedData = Object.values(clonedData);
  const arrangedData: Record<string, OrderedData> = {};
  sortedData.forEach((item) => {
    arrangedData[item?.uuid || ''] = {
      ...item,
      order_by: (item?.order_by || 0) > activeOrder ? (item.order_by || 0) + 1 : item.order_by,
    };
    if (activeUUID === item.uuid) {
      arrangedData[newUUID] = newData?.[newUUID] ?? (newData as unknown as OrderedData);
    }
  });
  return arrangedData;
};

interface TreeItem {
  name: string;
  children?: TreeItem[];
  [key: string]: unknown;
}

/**
 * Recursively searches and filters tree items
 */
export const searchFilter = (items: TreeItem[], term: string): TreeItem[] => {
  return items.reduce((acc: TreeItem[], item) => {
    if (contains(item.name, term)) {
      acc.push(item);
    } else if (item.children && item.children.length > 0) {
      const newItems = searchFilter(item.children, term);
      if (newItems && newItems.length > 0) {
        acc.push({
          ...item,
          children: newItems,
        });
      }
    }
    return acc;
  }, []);
};

const contains = (text: string, term: string): boolean => {
  return text.toLowerCase().indexOf(term?.toLowerCase()) >= 0;
};

/**
 * Options limit for dropdowns
 */
export const optionsLimit = 250;

/**
 * Checks if item is found in data array by uuid
 */
export const isFound = (id: string, data: Array<{ uuid?: string }>): boolean => {
  return (data || []).some((item) => item.uuid === id);
};

/**
 * Restricted keys for tables
 */
export const restrictedKeys = ['Name'];

/**
 * Gets catalog name from catalog object
 */
export const getCatalogName = (
  catalog: { name?: string } | null,
  isTrim: boolean = false
): string => {
  const { name } = catalog || {};
  return isTrim && typeof name === 'string'
    ? ((name || '')?.split('(')?.[0]?.trim() ?? '')
    : (name ?? '');
};

/**
 * Adds element at specific index in array
 */
export const addAtIndex = <T extends { para_index?: number }>(
  array: T[],
  index: number,
  element: T,
  from?: string
): T[] => {
  let data = [...array.slice(0, index), element, ...array.slice(index)];
  if (from === 'para') {
    data = data.map((item, idx) => ({
      ...item,
      para_index: idx,
    }));
  }
  return data;
};

interface ClusterData {
  uuid?: string;
  status?: string;
  cluster_size?: number;
  [key: string]: unknown;
}

/**
 * Gets cluster data for a specific catalog
 */
export const getClusterDataForCatalog = (
  clusters: ClusterData[],
  selClusterUuid: string | null
): ClusterData => {
  if (!clusters?.length) {
    return {};
  }
  let activeCluster: ClusterData | '' = '';
  let susCluster: ClusterData | '' = '';
  let selTabCluster: ClusterData | '' = '';
  let selTabActiveCluster: ClusterData | '' = '';

  customSortByKeyAsc(clusters || [], 'cluster_size').forEach((cluster) => {
    const active = cluster?.status === 'Running' || cluster?.status === 'Ready';
    const isSelClust = selClusterUuid === cluster?.uuid;
    if (isSelClust && active && !selTabActiveCluster) {
      selTabActiveCluster = cluster;
    } else if (isSelClust && !selTabCluster) {
      selTabCluster = cluster;
    } else if (active && !activeCluster) {
      activeCluster = cluster;
    } else if (!active && !susCluster) {
      susCluster = cluster;
    }
  });

  return selTabActiveCluster || activeCluster || selTabCluster || susCluster || {};
};

interface FilterableItem {
  status?: number;
  [key: string]: unknown;
}

/**
 * Checks if a value is a date
 */
const isDate = (value: unknown): boolean => {
  const dateTimeRegex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;
  return value instanceof Date || dateTimeRegex.test(value as string);
};

interface Column {
  field: string;
}

/**
 * Custom filter for table data
 */
export const customFilter = (
  data: FilterableItem[],
  keys: string[],
  columns: Column[],
  value: string
): FilterableItem[] => {
  try {
    const keysList = keys?.length === 0 ? (columns || []).map((item) => item?.field) : keys;
    const filteredData = data.filter((item) => {
      return keysList.some((key) => {
        const keyParts = key.split('.');
        let itemValue: unknown = keyParts.reduce(
          (obj, k) =>
            obj && (obj as Record<string, unknown>)[k] !== undefined
              ? (obj as Record<string, unknown>)[k]
              : undefined,
          item as unknown
        );

        if (isDate(itemValue)) {
          itemValue = formatdate(itemValue as Date, 'DD MMM YYYY');
        } else if (key === 'status') {
          itemValue = statusMsgMap[itemValue as number] || itemValue;
        }
        if (itemValue !== undefined && itemValue !== null) {
          const lowerCaseItemValue = (itemValue as string).toString().toLowerCase();
          const lowerCaseSearchValue = value.toString().toLowerCase();
          return lowerCaseItemValue.includes(lowerCaseSearchValue);
        }
        return false;
      });
    });

    return filteredData;
  } catch {
    return data;
  }
};

/**
 * Removes empty keys from an object recursively
 */
export function removeEmptyKeys<T extends Record<string, unknown>>(obj: T): T {
  for (const key in obj) {
    if (obj[key] && typeof obj[key] === 'object') {
      removeEmptyKeys(obj[key] as Record<string, unknown>);
      if (Object.keys(obj[key] as Record<string, unknown>).length === 0) {
        delete obj[key];
      }
    } else {
      if (obj[key] === undefined || obj[key] === null || obj[key] === '') {
        delete obj[key];
      }
    }
  }
  return obj;
}

// Stub config export for compatibility
export const config: Record<string, unknown> = {};

// ============================================
// V2 Utilities (from query-editor-v2)
// ============================================

// Re-export cn from @/lib/utils
export { cn } from '@/lib/utils';

// parseTooltipTitle is exported from './errors' via `export * from './errors'`

/**
 * Format bytes to human-readable string
 */
export function formatBytes(bytes: number, decimals = 2): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + (sizes[i] ?? 'Bytes');
}

/**
 * Format duration in milliseconds to human-readable string
 */
export function formatDuration(ms: number): string {
  if (ms < 1000) {
    return `${ms}ms`;
  }

  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
  }

  if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  }

  return `${seconds}s`;
}

/**
 * Debounce function
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };

    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };
}

/**
 * Generate a unique ID using cryptographically secure random values
 */
export function generateId(prefix = 'qe'): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return `${prefix}-${crypto.randomUUID().replace(/-/g, '').substring(0, 9)}`;
  }
  const array = new Uint8Array(5);
  crypto.getRandomValues(array);
  return `${prefix}-${Array.from(array, (b) => b.toString(36).padStart(2, '0'))
    .join('')
    .substring(0, 9)}`;
}

// ============================================
// V2 Helpers (from query-editor-v2/utils/helpers)
// ============================================

/**
 * Convert seconds to time format object
 */
const divMod = (x: number, y: number): [number, number] => [Math.floor(x / y), x % y];

const convertTimeFormat = (secs: number) => {
  let [days, hours, minutes, seconds] = [0, 0, 0, secs];
  const [newMins, newSec] = divMod(seconds, 60);
  if (newMins > 0) {
    seconds = newSec;
    minutes = newMins;
  }
  const [newHours, newMin] = divMod(minutes, 60);
  if (newHours > 0) {
    minutes = newMin;
    hours = newHours;
  }
  const [newDays, newHour] = divMod(hours, 24);
  if (newDays > 0) {
    hours = newHour;
    days = newDays;
  }
  return { days, hours, minutes, seconds };
};

/**
 * Format seconds to timer string (e.g., "1d 2h 3m 4s")
 */
export const timerString = (sec: number): string => {
  const { days, hours, minutes, seconds } = convertTimeFormat(sec) || {};
  return `${days ? days + 'd' : ''} ${hours ? hours + 'h' : ''} ${minutes ? minutes + 'm' : ''} ${
    seconds > -1 ? seconds + 's' : ''
  }`.trim();
};

export interface ResumeProgress {
  left: boolean;
  polls: number;
  // Start time of the cluster's latest Resume before this one was sent:
  // null when there was none, undefined when it couldn't be read.
  baseline?: string | null;
}

export interface ResumeActivity {
  operation?: string;
  status?: string;
  startedAt?: string;
  errors?: { title?: string; message?: string }[];
}

export interface ResumePollCluster {
  status?: string;
  message?: string;
  activityHistory?: ResumeActivity[];
}

/** The cluster's most recent Resume activity, or undefined. */
export const latestResume = (history?: ResumeActivity[]): ResumeActivity | undefined =>
  (history ?? [])
    .filter((a) => a.operation === 'Resume' && a.startedAt)
    .reduce<
      ResumeActivity | undefined
    >((latest, a) => (!latest || (a.startedAt ?? '') > (latest.startedAt ?? '') ? a : latest), undefined);

/**
 * One poll of an auto-resume wait. The operator re-suspends a resume that
 * fails, so the cluster reads Suspended again rather than Failed (CO-073).
 * A Resume activity newer than the baseline that failed is this attempt's
 * failure, however fast it failed. Without a baseline, a return to
 * Suspended after leaving it ends the wait. Never leaving Suspended within
 * maxPolls ends it too. Returns the updated progress and, when the wait
 * should end, the error to show.
 */
export const resumePollStep = (
  prev: ResumeProgress | undefined,
  cluster: ResumePollCluster,
  maxPolls = 12
): { progress: ResumeProgress; error?: string } => {
  const status = (cluster.status || '').toLowerCase();
  const progress: ResumeProgress = {
    left: prev?.left ?? false,
    polls: (prev?.polls ?? 0) + 1,
    baseline: prev?.baseline,
  };
  const failure = cluster.message || 'Cluster resume failed.';
  if (progress.baseline !== undefined) {
    const latest = latestResume(cluster.activityHistory);
    if (
      latest &&
      latest.startedAt !== progress.baseline &&
      latest.status?.toLowerCase() === 'failed'
    ) {
      const e = latest.errors?.[0];
      const reason = [e?.title, e?.message].filter(Boolean).join('. ');
      return { progress, error: reason || failure };
    }
  }
  if (status !== 'suspended') {
    return { progress: { ...progress, left: true } };
  }
  if (progress.left) {
    return { progress, error: failure };
  }
  if (progress.polls >= maxPolls) {
    return { progress, error: cluster.message || 'Cluster did not start resuming. Try again.' };
  }
  return { progress };
};

/**
 * Get executing status message based on query status
 */
export const getExecutingStatus = (queryStatus: string): string => {
  if (['Scheduling...'].includes(queryStatus)) {
    return 'Resuming e6data cluster...';
  } else if (['Connecting with engine...'].includes(queryStatus)) {
    return 'Connecting with e6data...';
  } else if (['Planning', 'Planning...'].includes(queryStatus)) {
    return 'e6data Planning...';
  } else if (['Fetching results', 'Fetching results...'].includes(queryStatus)) {
    return 'e6data Fetching results...';
  } else {
    return 'e6data Executing...';
  }
};

/**
 * Query result interface for time breakdown
 */
export interface V2QueryResult {
  parsingTime?: number;
  queuingTime?: number;
  execution_time?: number;
  [key: string]: unknown;
}

/**
 * Time breakdown data for tooltip display
 */
export interface TimeBreakdownData {
  wallTime: string | number;
  planningTime: string | number;
  queueTime: string | number;
  executionTime: string | number;
  transitTime: string | number;
}

/**
 * Get time breakdown data for display
 */
export const getTimeBreakdownData = (
  timeDiffms: number | string,
  queryResult: V2QueryResult | null | undefined
): TimeBreakdownData => {
  const timeDiff = typeof timeDiffms === 'string' ? parseFloat(timeDiffms) : timeDiffms;
  const parsing = queryResult?.parsingTime ?? 0;
  const queuing = queryResult?.queuingTime ?? 0;
  const execution = queryResult?.execution_time ?? 0;

  const transit = toFixDecimal(timeDiff - parsing - queuing - execution, 4);

  return {
    wallTime: `${timeDiffms} s`,
    planningTime: `${parsing} s`,
    queueTime: `${queuing} s`,
    executionTime: `${execution} s`,
    transitTime: `${Number(transit) > 0 ? transit : 0} s`,
  };
};
